import { ArtifactEditor } from "@/components/artifacts/ArtifactEditor.tsx";
import { useArtifactViewCtx } from "@/components/artifacts/view/ArtifactViewContext.tsx";
import { cn } from "@/lib/utils.ts";
import type { ArtifactComponent } from "@/types/artifacts-view.ts";

type MarkdownComponentData = Extract<ArtifactComponent, { type: "markdown" }>;

export const RichTextBodyBlock = ({ component }: { component: MarkdownComponentData }) => {
  const { isReadOnly, patchOne } = useArtifactViewCtx();
  const readOnly = isReadOnly || !component.editable;

  const save = async (next: string) => {
    await patchOne(component.editPath, next);
  };

  const editor = (
    <ArtifactEditor
      content={component.value}
      readOnly={readOnly}
      handleSave={save}
      showDebug={component.showDebug ?? false}
      hideToolbar={component.hideToolbar}
      imageSrcs={component.imageSrcs}
    />
  );

  // `boxed` wraps the editor in a bordered form field (routine-style cards) so
  // it reads as an editable textarea rather than bare prose.
  const body = component.boxed ? (
    <div
      className={cn(
        "rounded-md border bg-background px-3 py-2",
        "focus-within:ring-1 focus-within:ring-ring",
      )}
    >
      {editor}
    </div>
  ) : (
    editor
  );

  if (!component.label && !component.helpText) return body;

  return (
    <div className="flex flex-col gap-1">
      {/* Caption-style label (uppercase, muted) so it reads as a section
          heading distinct from the editor's own (possibly bold) content. */}
      {component.label ? (
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {component.label}
        </div>
      ) : null}
      {component.helpText ? (
        <div className="text-xs text-muted-foreground">{component.helpText}</div>
      ) : null}
      {body}
    </div>
  );
};
